/**
 * Linked-orders confirmation view scoped to CPT save (later-DOS filter).
 * @module useCptLinkedOrdersConfirmationView
 */
import { useMemo } from "react";
import { useLinkedOrdersModalView } from "../../../../../components/expandableTableForOrderAuth/hooks/useLinkedOrdersModalView";
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import { cptTableHasMappedSiblingStatus } from "../utils/cptSiblingStatusMappings";
import { filterLaterDosSiblingClubbedRows } from "../utils/filterLaterDosSiblingClubbedRows";

/**
 * Returns linked-order modal state for CPT save, excluding same/earlier-DOS
 * siblings when the table contains a mapped parent auth status.
 *
 * @param tableData - Live CPT table rows used to detect mapped statuses
 * @param parentDateOfService - Parent order date of service
 * @returns Linked-order modal state with DOS-filtered rows when mapped statuses
 *   are present, plus filtered `selectedSiblingOrderIds` aligned to visible rows
 */
export const useCptLinkedOrdersConfirmationView = (
  tableData: CptCodesTableRowModel[],
  parentDateOfService?: string,
) => {
  const linkedOrdersView = useLinkedOrdersModalView();
  const hasMappedSiblingStatus = cptTableHasMappedSiblingStatus(tableData);

  const expandableTableData = useMemo(() => {
    if (!hasMappedSiblingStatus) {
      return linkedOrdersView.expandableTableData;
    }

    return filterLaterDosSiblingClubbedRows(
      linkedOrdersView.expandableTableData,
      parentDateOfService,
    );
  }, [
    hasMappedSiblingStatus,
    linkedOrdersView.expandableTableData,
    parentDateOfService,
  ]);

  const visibleOrderIds = useMemo(
    () => new Set(expandableTableData.map((row) => row.order_id)),
    [expandableTableData],
  );

  const selectedSiblingOrderIds =
    linkedOrdersView.selectedSiblingOrderIds.filter((orderId) =>
      visibleOrderIds.has(orderId),
    );

  return {
    ...linkedOrdersView,
    expandableTableData,
    selectedSiblingOrderIds,
    hasLinkedOrders: expandableTableData.length > 0,
  };
};
